import { DataLayerAction } from '@corratech/pylot-tag-manager/src/TagContext.d'
import { useCart } from '@corratech/pylot-cart-manager'
import { useObjectCompare } from '@corratech/pylot-tag-manager/src/hooks/useObjectCompare'
import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import { Cart } from '@pylot-data/pylotschema'
import { identifyCartOperationName } from '@corratech/pylot-tag-manager/src/TrackCartState/identifyCartOperationName'
import { identifyCartProductsCategories } from '@corratech/pylot-tag-manager/src/TrackCartState/identifyCartProductsCategories'
import { identifyUpdatedItems } from '@corratech/pylot-tag-manager/src/TrackCartState/identifyUpdatedItems'
import stringify from 'fast-json-stable-stringify'
import { useAlfakherContext } from '@components/alfakherContext/AlfakherContext'
import { UpsellPromotionAdditionalData } from 'stores/ooka_uae_store/components/OOKA/pylot-gtm/src/GTMEvents/pushUpsellPromotionView'

type localItemType = {
    sku: string
}

export const TrackCartState = ({
    dataLayerAction
}: {
    dataLayerAction: DataLayerAction
}): null => {
    const {
        isFallback,
        query: { categoryName, url_path }
    } = useRouter()
    const trackedCartState = useRef<Cart | undefined>()
    const { data } = useCart({
        revalidateOnFocus: false,
        revalidateOnMount: false,
        revalidateOnReconnect: false
    })
    const cart = data?.data?.cart
    const isOptimistic = data?.data?.isOptimistic
    const hasChanges = useObjectCompare({}, ['items', 'prices'])

    // @ts-ignore
    const { gtmLocation, setGTMLocation } = useAlfakherContext()

    const gtmLocations = 'UPSELL_PROMOTION'

    useEffect(() => {
        const lastThreeEvents = window.dataLayer?.slice(-3) ?? []
        const purchaseEvent = lastThreeEvents.find(
            (item: any) => item[1] === 'purchase'
        )

        if (
            cart &&
            !isFallback &&
            !isOptimistic &&
            hasChanges(cart, true) &&
            !purchaseEvent &&
            cart.items
        ) {
            const updatedItems = identifyUpdatedItems(
                cart,
                trackedCartState.current
            )
            const localItems =
                JSON.parse(localStorage.getItem('addedToCartItems') || '[]') ||
                []

            const cartSkus = cart.items.map((item) => item?.product?.sku)

            const filteredLocalItems = localItems.filter(
                (localItem: localItemType) => cartSkus.includes(localItem.sku)
            )

            localStorage.setItem(
                'addedToCartItems',
                JSON.stringify(filteredLocalItems)
            )
            const enrichedItems = cart.items.map((cartItem) => {
                const match = localItems.find(
                    (localItem: localItemType) =>
                        localItem.sku === cartItem?.product.sku
                )
                return match
                    ? {
                          ...cartItem,
                          ...UpsellPromotionAdditionalData
                      }
                    : cartItem
            })

            const enrichedCart = {
                ...cart,
                items: enrichedItems
            }

            dataLayerAction({
                type: identifyCartOperationName(cart, trackedCartState.current),
                data: {
                    ...enrichedCart,
                    updatedItems: identifyCartProductsCategories(
                        updatedItems,
                        url_path,
                        categoryName
                    )
                }
            })
            if (gtmLocations === gtmLocation) {
                dataLayerAction({
                    type: 'UPSELL_PROMOTION_SELECTED',
                    data: { upsell_products: updatedItems }
                })
                setGTMLocation('')
            }
            // Saved cart state needs to be stripped of internal references to the current state
            trackedCartState.current = JSON.parse(
                // @ts-ignore - options are supported but not declared in the definition
                stringify(cart, { cycles: true })
            )
        }
    }, [cart, isFallback, isOptimistic])

    return null
}
